import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Update } from '@ngrx/entity';
import { select, Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { Property, TrainingStatus } from 'src/app/models';
import { SettingsService } from '../../settings/services/settings.service';
import { currentOrganization } from '../auth';
import * as AuthActions from '../auth/auth.actions';
import { AppState } from '../core.state';
import * as PropertyActions from './properties.actions';
import { selectCurrentPropertyId, selectPropertyById } from './properties.selectors';
import * as ToastrActions from './toastr.actions';
import * as KbActions from '../../kb/store/kb.actions';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CharlaScrapedPagePreviewComponent } from 'src/app/settings/scraped-page-preview/scraped page-preview.component';
import { FacebookPageSelectorComponent } from 'src/app/settings/facebookpage-selector/facebookpage-selector.component';


@Injectable()
export class PropertiesEffects {

    saveProperty = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.PropertySave),
        mergeMap(action => combineLatest( [
                of(action),
                this.store.pipe( select(currentOrganization), filter(o => o != null), take(1) )
            ])
        ),
        mergeMap( ([action, org]) => 
            this.settingsService.saveProperty(action.property, org.id).pipe(
                switchMap(res => [
                    PropertyActions.PropertySaveSuccess({property: res.model}),
                    ToastrActions.showToastrSuccess({message: 'Property was successfully saved.', title: ''}),
                    // Refresh the user properties after we added a new one
                    AuthActions.UserRequested(),
                    // Refresh the isKbWidget flag in case the save is initiated from the quick wizard
                    KbActions.KbRequest()
                ]),
                catchError((error: HttpErrorResponse) => of( 
                    PropertyActions.PropertySaveFailed({message: error.error}),
                    ToastrActions.showToastrError({message: error.error , title: 'Something went wrong'})
                ))
            )
        )
    ));

    savePropertyFailed = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.PropertySaveFailed),
        mergeMap(action => of(ToastrActions.showToastrError({
            message: 'Error occured while saving property.' , 
            title: 'Something went wrong'
        })))
    ));

    deleteProperty = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.PropertyDeleteRequested),
        mergeMap( action => 
            this.settingsService.deleteProperty(action.id).pipe(
                switchMap(res => [
                    PropertyActions.PropertyDeleteSucess({ id: action.id }),
                    ToastrActions.showToastrSuccess({message: 'Property was successfully deleted.', title: ''})
                ]),
                catchError((error: HttpErrorResponse) => of(
                    PropertyActions.PropertyDeleteFailed({message: error.error}),
                    ToastrActions.showToastrError({message: error.error , title: 'Something went wrong'})
                ))
            )
        )
    ));

    deletePropertyFailed = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.PropertyDeleteFailed),
        mergeMap(action => of(ToastrActions.showToastrError({
            message: 'Error occured while deleting property.' , 
            title: 'Something went wrong'
        })))
    ));

    saveFacebookConnect = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.FacebookConnectRequest),
        mergeMap(action => 
            this.settingsService.retrieveFacebookPages(action.token, action.user_id, action.property_id).pipe(
                switchMap(res => {
                    const modalRef = this.modalService.open(FacebookPageSelectorComponent, { centered: true });
                    modalRef.componentInstance.pages = res.model;
                    modalRef.componentInstance.property_id = action.property_id;
                    return [PropertyActions.FacebookConnectSuccess()]
                } ),
                catchError((err: HttpErrorResponse) => of(
                    PropertyActions.FacebookConnectFailed({message: err.error.detail}),
                    ToastrActions.showToastrError({message: err.error.detail, title: 'Error Retrieving Pages List'})
                ))
            )
        )
    ));

    subscriveFacebookPages = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.FacebookSubscribeRequest),
        mergeMap(action => 
            this.settingsService.subscribeFacebookPages(action.pages, action.property_id).pipe(
                switchMap(res => {
                    const property_update: Update<Property> = {
                        id: action.property_id,
                        changes: {
                            facebook_pages: res.model
                        }
                    };
                    return [
                        PropertyActions.FacebookSubscribeSuccess({property: property_update }),
                        ToastrActions.showToastrSuccess({message: 'Facebook page was connected', title: ''})
                    ];
                }),
                catchError((err: HttpErrorResponse) => of(
                    PropertyActions.FacebookConnectFailed({message: err.error.detail}),
                    ToastrActions.showToastrError({message: err.error.detail, title: 'Error Subscribing to Facebook Pages'})
                ))
            )
        )
    ));


    unlinkFacebookPage = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.FacebookUnlinkRequest),
        mergeMap(action => this.settingsService.unlinkFacebookPage(action.page_id).pipe(
            switchMap(res => {
                const property_update: Update<Property> = {
                    id: action.property_id,
                    changes: {
                        facebook_pages: res.model
                    }
                };
                return [
                    PropertyActions.FacebookUnlinkSuccess({property: property_update }),
                    ToastrActions.showToastrSuccess({message: 'Facebook page was unlinked successfully', title: ''})
                ];
            }),
            catchError((error: HttpErrorResponse) => of(
                PropertyActions.FacebookUnlinkFailed({message: error.error }),
                ToastrActions.showToastrError({message: "Your Facebook page was deleted from Charla but we faced an issue unsubscribing the page with Facebook. " + error.error.detail , title: 'Problems Unlinking your Facebook page'})
                ))
        ))
    ));

    saveInstagramConnect = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.InstagramConnectRequest),
        mergeMap(action => 
            this.settingsService.connectInstagram(action.code, action.property_id).pipe(
                switchMap(res => {
                    const property_update: Update<Property> = {
                        id: action.property_id,
                        changes: {
                            instagram_accounts: res.model
                        }
                    };
                    
                    return [
                        PropertyActions.InstagramConnectSuccess({property: property_update }),
                        ToastrActions.showToastrSuccess({message: 'Instagram account was connected', title: ''})
                    ];
                }),
                catchError((error: HttpErrorResponse) => of(
                    PropertyActions.InstagramConnectFailed({message: error.status == 400 ?
                        "Instagram Account is already linked with another property in or outside your organization.": "Error Connecting Instagram Account"}),
                    ToastrActions.showToastrError({message: error.status == 400 ?
                        "Instagram Account is already linked with another property in or outside your organization.": "Error Connecting Instagram Account" , 
                        title: 'Something went wrong'})
                ))
            )
        )
    ));

    unlinkInstagramPage = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.InstagramUnlinkRequest),
        mergeMap(action => this.settingsService.unlinkInstagramAccount(action.account_id).pipe(
            switchMap(res => {
                const property_update: Update<Property> = {
                    id: action.property_id,
                    changes: {
                        instagram_accounts: res.model
                    }
                };
                return [
                    PropertyActions.InstagramUnlinkSuccess({property: property_update }),
                    ToastrActions.showToastrSuccess({message: 'Instagram Account was unlinked successfully', title: ''})
                ];
            }),
            catchError((error: HttpErrorResponse) => of(
                PropertyActions.InstagramUnlinkFailed({message: error.error }),
                ToastrActions.showToastrError({message: "Your Instagram account was deleted from Charla but we faced an issue unsubscribing with Instagram. " + error.error.detail , title: 'Problems Unlinking your Instagram account'})
                ))
        ))
    ));

    saveWhatsappConnect = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.WhatsappConnectRequest),
        mergeMap(action => 
            this.settingsService.connectWhatsappNumber(action.code, action.property_id, action.waba_id, action.phone_number_id).pipe(
                switchMap(res => {
                    const property_update: Update<Property> = {
                        id: action.property_id,
                        changes: {
                            whatsapp_numbers: res.model
                        }
                    };
                    
                    return [
                        PropertyActions.WhatsappConnectSuccess({property: property_update }),
                        ToastrActions.showToastrSuccess({message: 'Your WhatsApp number is now linked to your property', title: ''})
                    ];
                }),
                catchError((error: HttpErrorResponse) => of(
                    PropertyActions.WhatsappConnectFailed({message: error.error}),
                    ToastrActions.showToastrError({message: error.error.detail ?? "We weren't able to register the number with WhatsApp Api. Please contact supprt." , 
                        title: 'Problems Linking to your WhatsApp number'})
                ))
            )
        )
    ));

    unlinkWhatsappNumber = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.WhatsappUnlinkRequest),
        mergeMap(action => this.settingsService.unlinkWhatsappNumber(action.number_id).pipe(
            switchMap(res => {
                const property_update: Update<Property> = {
                    id: action.property_id,
                    changes: {
                        whatsapp_numbers: res.model
                    }
                };
                return [
                    PropertyActions.WhatsappUnlinkSuccess({property: property_update }),
                    ToastrActions.showToastrSuccess({message: 'WhatsApp phone number was unlinked successfully', title: ''})
                ];
            }),
            catchError((error: HttpErrorResponse) => of(
                PropertyActions.WhatsappUnlinkFailed({message: error.error }),
                ToastrActions.showToastrError({message: "Number was deleted from Charla but we faced an issue unsubscribing with WhatsApp. " + error.error.detail , title: 'Problems Unlinking your WhatsApp number'})
                ))
        ))
    ));

    saveTelegramConnect = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.TelegramConnectRequest),
        mergeMap(action => 
            this.settingsService.connectTelegram(action.bot_token, action.property_id).pipe(
                withLatestFrom(this.store.pipe(select(selectPropertyById({property_id: action.property_id})))),
                switchMap(([res, selected_property]) => {
                    const property_update: Update<Property> = {
                        id: action.property_id,
                        changes: {
                            telegram_bots: [...(selected_property.telegram_bots ?? []), res]
                        }
                    };
                    
                    return [
                        PropertyActions.TelegramConnectSuccess({property: property_update }),
                        ToastrActions.showToastrSuccess({message: 'Telegram bot was connected successfully', title: ''})
                    ];
                }),
                catchError((error: HttpErrorResponse) => of(
                     ToastrActions.showToastrError({message: error.error?.detail ?? "Error connecting Telegram bot. Please check your bot token." , 
                        title: 'Something went wrong'})
                ))
            )
        )
    ));

    unlinkTelegramBot = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.TelegramUnlinkRequest),
        mergeMap(action => this.settingsService.unlinkTelegramBot(action.bot_id).pipe(
            withLatestFrom(this.store.pipe(select(selectPropertyById({property_id: action.property_id})))),
            switchMap(([res, selected_property]) => {
                const property_update: Update<Property> = {
                    id: action.property_id,
                    changes: {
                        telegram_bots: (selected_property.telegram_bots ?? []).filter(tb => tb.id !== action.bot_id)
                    }
                };
                return [
                    PropertyActions.TelegramUnlinkSuccess({property: property_update }),
                    ToastrActions.showToastrSuccess({message: 'Telegram bot was unlinked successfully', title: ''})
                ];
            }),
            catchError((error: HttpErrorResponse) => of(
                PropertyActions.TelegramUnlinkFailed({message: error.error }),
                ToastrActions.showToastrError({message: "We encountered an issue unlinking the bot. Please try again." , title: 'Something went wrong'})
                ))
        ))
    ));

    documentDeleteRequest = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.DocumentDeleteRequested),
        mergeMap(action => this.settingsService.deleteDocument(action.id).pipe(
            switchMap(res => [
                PropertyActions.DocumentDeleteSuccess({id: action.id, property_id: action.property_id}),
                ToastrActions.showToastrSuccess({message: 'Document was successfully deleted.' , title: ''})
            ]),
            catchError((er: HttpErrorResponse) => of(
                PropertyActions.DocumentDeleteFailed({message: 'Problems while deleting the document' }),
                ToastrActions.showToastrError({message: 'Problems while deleting the document. The error has been reported.' , title: 'Something went wrong'})
            ))
        ))
    ));

    documentUploadRequest = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.DocumentUploadRequested),
        mergeMap(action => this.settingsService.uploadDocument(action.file, action.property_id).pipe(
            mergeMap(res => {
                
                if (res.type == HttpEventType.UploadProgress) {
                    const prog = Math.round(100 * (res.loaded / res.total));
                    return of(PropertyActions.SetUploadProgress({progress: prog}));
                } else if (res.type == HttpEventType.Response){
                    return of(
                        PropertyActions.DocumentUploadSuccess({doc: res.body.model}),
                        ToastrActions.showToastrSuccess({message: 'Document was successfully uploaded.' , title: ''})
                    );
                } else {
                    return of(PropertyActions.SetUploadProgress({progress: 0}));
                }
            }),
            catchError((er: Error | HttpErrorResponse) => of(
                PropertyActions.DocumentUploadFailed({message: 'Problems while uploading the document' }),
                ToastrActions.showToastrError({message: (er instanceof  HttpErrorResponse) ? 'Problems while uploading the document. The error has been reported.' : er.message, title: 'Something went wrong'})
            ))
        ))
    ));

    retrainWebpageRequest = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.RetrainWebpageRequest),
        mergeMap(action => this.settingsService.retrainWebpage(action.id).pipe(
            switchMap(res => [
                PropertyActions.SetWebpagesTrainingStatus({ webpages: [{ external_id: action.external_id, status: TrainingStatus.InProgress }], property_id: action.property_id }),
                ToastrActions.showToastrSuccess({message: 'Webpage retraining has been initiated.' , title: ''})
            ]),
            catchError((er: HttpErrorResponse) => of(
                ToastrActions.showToastrError({message: 'Problems while retraining the webpage. The error has been reported.' , title: 'Something went wrong'})
            ))
        ))
    ));

    smtpTest = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.SMTPTestRequest),
        mergeMap(action => this.settingsService.smtpTest(action.host, action.port, action.username, action.password, action.from).pipe(
            switchMap(res => [
                ToastrActions.showToastrSuccess({message: 'Charla successfully connected through SMTP. Please check your email for the test message.' , title: 'Email Sent'}),
                PropertyActions.SMTPTestSuccess(),
            ]),
            catchError((er: HttpErrorResponse) => of(
                ToastrActions.showToastrError({message: 'Problems while connecting through SMTP. Please make you are connecting through the TLS ports, and your username and password are correct.' , title: 'SMTP Connection Issue'}),
                PropertyActions.SMTPTestFailed({message: er.message})
            ))
        ))
    ));

    mailboxGoogleConnect = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.MailboxGoogleConnectRequest),
        mergeMap(action => 
            this.settingsService.connectMailboxGoogle(action.code, action.property_id).pipe(
                switchMap(res => {
                    const property_update: Update<Property> = {
                        id: action.property_id,
                        changes: {
                            connected_mailbox: res.model.email,
                            sending_mode: 2
                        }
                    };
                    
                    return [
                        PropertyActions.MailboxGoogleConnectSuccess({property: property_update }),
                        ToastrActions.showToastrSuccess({message: 'Mailbox was connected', title: ''})
                    ];
                }),
                catchError((error: HttpErrorResponse) => of(
                    ToastrActions.showToastrError({message: "We were not able to connect to your Google mailbox. The error has been reported.",
                        title: 'Something went wrong'})
                ))
            )
        )
    ));

    unlinkMailbox = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.MailboxUnlinkRequest),
        mergeMap(action => this.settingsService.unlinkMailbox(action.property_id).pipe(
            switchMap(res => {
                const property_update: Update<Property> = {
                    id: action.property_id,
                    changes: {
                        connected_mailbox: null
                    }
                };
                return [
                    PropertyActions.MailboxUnlinkSuccess({property: property_update }),
                    ToastrActions.showToastrSuccess({message: 'Mailbox was unlinked successfully', title: ''})
                ];
            }),
            catchError((error: HttpErrorResponse) => of(
                PropertyActions.MailboxUnlinkFailed({message: "We encountered an issue unlinking the mailbox. Please try again." }),
                ToastrActions.showToastrError({message: "We encountered an issue unlinking the mailbox. Please try again." , title: 'Something went wrong'})
                ))
        ))
    ));

    emailTemplatesRequest = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.EmailTemplateRequest),
        withLatestFrom(this.store.select(selectCurrentPropertyId)),
        mergeMap(([action, property_id]) => 
            this.settingsService.getEmailTemplates(property_id).pipe(
                switchMap(res => [
                    PropertyActions.EmailTemplateSuccess({emailTemplates: res.model, property_id})
                ]),
                catchError((er: HttpErrorResponse) => of(
                    ToastrActions.showToastrError({message: 'Problems while loading email templates. Please try again later.' , title: 'Email Templates Issue'}),
                    PropertyActions.EmailTemplateFailed({message: er.message})
                ))
            )
        )
    ));

    emailTemplateSave = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.EmailTemplateSaveRequest),
        withLatestFrom(this.store.select(selectCurrentPropertyId)),
        mergeMap(([action, property_id]) => 
            this.settingsService.saveEmailTemplate({ ...action.emailTemplate, property_id }).pipe(
                switchMap(res => [
                    PropertyActions.EmailTemplateSaveSuccess({ emailTemplate: res.model }),
                    ToastrActions.showToastrSuccess({message: 'Email template was successfully saved.' , title: ''})
                ]),
                catchError((er: HttpErrorResponse) => of(
                    ToastrActions.showToastrError({message: 'Problems while saving email template. Please try again.' , title: 'Email Template Issue'}),
                    PropertyActions.EmailTemplateSaveFailed({message: er.message})
                ))
            )
        )
    ));


    emailTemplateReset = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.EmailTemplateResetRequest),
        withLatestFrom(this.store.select(selectCurrentPropertyId)),
        mergeMap(([action, property_id]) => 
            this.settingsService.resetEmailTemplate({...action.emailTemplate, property_id}).pipe(
                switchMap(res => [
                    PropertyActions.EmailTemplateResetSuccess({emailTemplate: res.model}),
                    ToastrActions.showToastrSuccess({message: 'Email template was successfully reset.' , title: ''})

                ]),
                catchError((er: HttpErrorResponse) => of(
                    ToastrActions.showToastrError({message: 'Problems while resetting email template. Please try again later.' , title: 'Email Template Issue'}),
                    PropertyActions.EmailTemplateResetFailed({message: er.message})
                ))
            )
        )
    ));

    scrapedWebpageRequest = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.ScrapedWebpageContentRequest),
        mergeMap(action => this.settingsService.getScrapedWebpage(action.page_id).pipe(
            switchMap(res => [
                PropertyActions.ScrapedWebpageContentSuccess({content: res.model.content})
            ]),
            catchError((er: HttpErrorResponse) => of(
                ToastrActions.showToastrError({message: 'Problems while retrieving scraped webpage content. Please try again later.' , title: 'Scraped Webpage Issue'}),
                PropertyActions.ScrapedWebpageContentFailed({message: er.message})
            ))
        ))
    ));

    showScrapedPagePreview = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.ScrapedWebpageContentSuccess),
        tap(s => {
            const modalRef = this.modalService.open(CharlaScrapedPagePreviewComponent, { centered: true, windowClass: 'charla-dialog-xl' });
            modalRef.componentInstance.scraped_page = s.content;
        })
    ), { dispatch: false });

    saveFlow = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.FlowSaveRequest),
        mergeMap(action => 
            this.settingsService.saveFlow(action.flow).pipe(
                switchMap(res => [
                    PropertyActions.FlowSaveSuccess({flow: res}),
                    ToastrActions.showToastrSuccess({message: 'Flow was successfully saved.' , title: ''})
                ] ),
                catchError((err: HttpErrorResponse) => of(
                    PropertyActions.FlowSaveFailed({message: err.error.detail}),
                    ToastrActions.showToastrError({message: err.error.detail, title: 'Error Saving Flow'})
                ))
            )
        )
    ));

    deleteFlow = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.FlowDeleteRequest),
        mergeMap(action => 
            this.settingsService.deleteFlow(action.flow_id).pipe(
                switchMap(res => [
                    PropertyActions.FlowDeleteSuccess({flow_id: action.flow_id, property_id: action.property_id}),
                    ToastrActions.showToastrSuccess({message: 'Flow was successfully deleted.' , title: ''})
                ]),
                catchError((err: HttpErrorResponse) => of(
                    PropertyActions.FlowDeleteFailed({message: err.error?.detail ?? 'Error deleting flow'}),
                    ToastrActions.showToastrError({message: err.error?.detail ?? 'Error deleting flow', title: 'Error Deleting Flow'})
                ))
            )
        )
    ));

    toggleFlow = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.FlowToggleRequest),
        mergeMap(action => 
            this.settingsService.toggleFlow(action.flow_id).pipe(
                switchMap((res: any) => [
                    PropertyActions.FlowToggleSuccess({flow_id: res.id, is_enabled: res.is_enabled, property_id: res.property_id}),
                    ToastrActions.showToastrSuccess({message: 'Flow status updated successfully.' , title: ''})
                ]),
                catchError((err: HttpErrorResponse) => of(
                    PropertyActions.FlowToggleFailed({message: err.error?.detail ?? 'Error toggling flow'}),
                    ToastrActions.showToastrError({message: err.error?.detail ?? 'Error toggling flow', title: 'Error Updating Flow'})
                ))
            )
        )
    ));

    getProducts = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.ProductsRequest, PropertyActions.ProductTrainingStatusUpdate),
        mergeMap(action => {
            const page = 'page' in action ? action.page ?? 1 : 1;
            const limit = 'limit' in action ? action.limit ?? 20 : 20;
            return this.settingsService.getProducts(action.property_id, page, limit).pipe(
                switchMap(res => [
                    PropertyActions.ProductsRequestSuccess({
                        products: res.model,
                        property_id: action.property_id,
                        page: res.pageNumber,
                        limit: res.pageSize,
                        total: res.itemsCount
                    })
                ]),
                catchError((err: HttpErrorResponse) => of(
                    PropertyActions.ProductsRequestFailed({message: err.error?.message ?? 'Error loading products'}),
                    ToastrActions.showToastrError({message: err.error?.message ?? 'Error loading products', title: 'Error'})
                ))
            )
        })
    ));

    constructor(
        private settingsService: SettingsService,
        private actions$: Actions,
        private store: Store<AppState>,
        private modalService: NgbModal
    ) { }

}